import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router/dist";
import { useSearchParams } from "react-router-dom";
import { useCart } from "../../../contexts/Cart/CartContext";
import {
  ClothingResponse,
  SizeOptions,
  ClothingType,
} from "../../../Interfaces/Clothing";
import { createEmptyCartItemWithCustom } from "../../../Interfaces/Types";
import { CartItem } from "../../../Interfaces/Types";
import AddToCartButton from "../AddToCartButton";
import "./CustomClothing.css";
import config from "../../../config";
import EmptyParticipantShop from "../MissingParticipant";
import { decodeQueryParameter } from "../../../CommonFunctions";

type CustomizationOption = {
  text: string;
  upcharge: number;
};

const ClothingForm = () => {
  // States for clothing data

  const customizationOptions: CustomizationOption[] = [
    { text: "No Text on Back - $0", upcharge: 0 },
    { text: "Name - $6", upcharge: 6 },
    { text: "Name and Number - $10", upcharge: 10 },
    { text: "Cheer Bow and Vertical Name - $6", upcharge: 6 },
  ];

  const [clothingData, setClothingData] = useState<ClothingResponse[]>([]);
  const [selectedType, setSelectedType] = useState<"Shirt" | "Hoodie" | "">("");
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [availableSizes, setAvailableSizes] = useState<SizeOptions[]>([]);
  const [selectedItem, setSelectedItem] = useState<ClothingType | null>(null);
  const [customizationOption, setCustomizationOption] =
    useState<CustomizationOption>(customizationOptions[0]);
  const handleCustomizationOptionChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedText = event.target.value;
    const option = customizationOptions.find(
      (opt) => opt.text === selectedText
    );
    if (option) {
      setCustomizationOption(option);
    }
    if (selectedText === "No Text on Back - $0") {
      setClothingText("");
    }
  };
  const [clothingItem, setClothingItem] = useState<CartItem>(
    createEmptyCartItemWithCustom()
  );
  const [clothingText, setClothingText] = useState<string>("");
  const { state } = useCart();
  const [searchParams] = useSearchParams();
  const encodedImageLocation = searchParams.get("imageLocation");
  const decodedImageLocation =
    decodeQueryParameter(encodedImageLocation || "")["imageURL"] || "";
  const navigate = useNavigate();

  const [quantity, setQuantity] = useState<number>(1);
  const colorHexMap: Record<string, string> = {
    Black: "#000000",
    Gray: "#6c757d",
    Navy: "#191f2b",
    Red: "#ff0000",
    Royal: "#4169e1",
    White: "#dddddd",
    Burgundy: "#80426b", // extra colors based on bottles, just in case
    Green: "#005c30",
    Purple: "#a179ae",
    Light_Blue: "#90d4e9",
    Pink: "#f064a5",
  };

  useEffect(() => {
    // Fetch clothing data from the API
    const fetchClothingData = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}storefront/categorized-products/clothing/`
        );
        const data = await response.json();
        setClothingData(data);
      } catch (error) {
        console.error("Failed to fetch clothing data:", error);
      }
    };

    fetchClothingData();
  }, []);

  useEffect(() => {
    if (clothingData.length > 0) {
      // Auto-select the first available type, color, and size
      const firstClothingItem = clothingData[0].clothing_options[0];

      // Set the default selected values based on the first option
      setSelectedType(clothingData[0].type);
      setSelectedColor(firstClothingItem.color);
      setAvailableSizes(firstClothingItem.size_options);
      setSelectedSize(firstClothingItem.size_options[0].db_name);
      setSelectedItem(firstClothingItem);

      // Setup initial cart item based on the first option
      const initialCartItem = createEmptyCartItemWithCustom();
      if (initialCartItem.customization) {
        initialCartItem.quantity = quantity;
        initialCartItem.image = `${firstClothingItem.image_url}`;
        initialCartItem.price = Number(firstClothingItem.size_options[0].cost);
        initialCartItem.sku = firstClothingItem.size_options[0].sku;
        initialCartItem.customization.clothing_size =
          firstClothingItem.size_options[0].db_name;
        initialCartItem.customization.design = decodedImageLocation;
        initialCartItem.customization.bottle_text = clothingText;
      }

      setClothingItem(initialCartItem); // Initialize the cart item state
    }
  }, [clothingData, decodedImageLocation]);

  useEffect(() => {
    // Update clothing item with the design and imageLocation
    setClothingItem((prevItem) => ({
      ...prevItem,
      customization: {
        ...prevItem.customization,
        design: decodedImageLocation,
      },
    }));
  }, [decodedImageLocation]);

  useEffect(() => {
    if (selectedItem && selectedSize) {
      const selectedSizeObj = selectedItem.size_options.find(
        (size: any) => size.db_name === selectedSize
      );

      if (selectedSizeObj) {
        setClothingItem((prevItem) => ({
          ...prevItem,
          sku: selectedSizeObj.sku,
          customization: {
            ...prevItem.customization,
            clothing_size: selectedSize,
            design: decodedImageLocation,
            clothing_customization: customizationOption.text,
          },
          price:
            Number(selectedSizeObj?.cost || 0) + customizationOption.upcharge,
        }));
      }
    }
  }, [selectedSize, selectedItem, customizationOption]);

  useEffect(() => {
    const clothingType = clothingData.find(
      (item) => item.type === selectedType
    );

    if (clothingType) {
      const selectedClothing = clothingType.clothing_options.find(
        (item) => item.color === selectedColor
      );

      if (selectedClothing) {
        setSelectedItem(selectedClothing);
        setAvailableSizes(selectedClothing.size_options);

        // Check if the previously selected size exists in the new color selection
        const sizeExists = selectedClothing.size_options.some(
          (size) => size.db_name === selectedSize
        );

        if (sizeExists) {
          // Size exists, update the cart item name and details
          const selectedSizeObj = selectedClothing.size_options.find(
            (size) => size.db_name === selectedSize
          );

          setClothingItem((prevItem) => ({
            ...prevItem,
            name: `${selectedClothing.color} ${clothingType.type} - ${
              selectedSizeObj?.display_name || "Unknown Size"
            }`,
            sku: selectedSizeObj?.sku || -1,
            image: `${selectedClothing.image_url}`,
            price:
              Number(selectedSizeObj?.cost || 0) + customizationOption.upcharge,
            customization: {
              ...prevItem.customization,
              clothing_size: selectedSize,
              bottle_text: clothingText,
            },
          }));
        } else {
          // Size doesn't exist, set name with no size, price to 0, and clear size selection
          setSelectedSize("");
          setClothingItem((prevItem) => ({
            ...prevItem,
            name: `${selectedClothing.color} ${clothingType.type} - No Size Selected`,
            sku: -1,
            image: `${selectedClothing.image_url}`,
            price: 0,
            customization: {
              ...prevItem.customization,
              clothing_size: "",
              bottle_text: clothingText,
            },
          }));
        }
      }
    }
  }, [selectedType, selectedColor, clothingData, clothingText]);

  // Empty handler for "Continue Shopping" button
  const handleContinueShopping = () => {
    const participant_id = state.fundraiser_participant?.participant_code;
    navigate(`/participant-shop/part-id/${participant_id}`);
  };
  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const cur_quantity = parseInt(event.target.value, 10) || 1;
    setQuantity(cur_quantity);

    setClothingItem((prevItem) => ({
      ...prevItem,
      quantity: cur_quantity,
    }));
  };
  const handleSizeChange = (size: string) => {
    setSelectedSize(size);

    const selectedSizeObj = selectedItem?.size_options.find(
      (s) => s.db_name === size
    );

    if (selectedSizeObj) {
      setClothingItem((prevItem) => ({
        ...prevItem,
        name: `${selectedItem?.color} ${selectedType} - ${selectedSizeObj.display_name}`,
        price:
          Number(selectedSizeObj?.cost || 0) + customizationOption.upcharge,
        customization: {
          ...prevItem.customization,
          clothing_size: size,
        },
      }));
    } else {
      // If size is not found, set price to 0 and indicate missing size
      setClothingItem((prevItem) => ({
        ...prevItem,
        name: `${selectedItem?.color || ""} ${selectedType} - No Size Selected`,
        price: 0,
        customization: {
          ...prevItem.customization,
          clothing_size: "",
        },
      }));
    }
  };

  if (!state.fundraiser_participant) {
    return <EmptyParticipantShop />;
  }

  return (
    <div className="store-front-customize-shirts-container">
      <div className="store-front-customize-shirts-image-wrapper">
        {/* Base clothing image */}
        <img
          className="store-front-customize-shirts-image"
          src={`${selectedItem?.image_url}`}
          alt={selectedColor}
        />
        {/* Overlay image with conditional class based on clothing type */}
        <img
          className={`store-front-customize-shirts-overlay ${selectedType.toLowerCase()}`} // Apply either .shirt or .hoodie class
          src={decodedImageLocation}
          alt="Overlay Design"
        />
        {/* Disclaimer - Now inside the image wrapper */}
        <p className="store-front-customize-shirts-disclaimer">
          * Please note that the final product may vary slightly from the images
          shown depending on screen settings and design adjustments.
          <br />
          **Design colors may be adjusted depending on the garment color
          selected. (IE a black logo will be turned to white if a black garment
          is chosen.)
        </p>
      </div>
      <div className="store-front-customize-shirts-controls">
        <h1>Clothing Customization</h1>
        {/* Type selection buttons */}
        <label className="store-front-customize-shirts-label">
          Choose a clothing type:
        </label>
        <div className="store-front-customize-shirts-options">
          {clothingData.map((item) => (
            <button
              key={item.type}
              className={`store-front-customize-shirts-button ${
                selectedType === item.type ? "selected" : ""
              }`}
              onClick={() => {
                setSelectedType(item.type);
                setSelectedColor(item.clothing_options[0].color); // Reset color when changing type
              }}
            >
              {item.type}
            </button>
          ))}
        </div>

        {/* Color selection */}
        <label className="store-front-customize-shirts-label">
          Choose a color:
        </label>
        <div className="store-front-customize-shirts-options">
          {clothingData
            .find((item) => item.type === selectedType)
            ?.clothing_options.map((item, index) => (
              <div
                className="store-front-customize-shirts-option-container"
                key={index}
              >
                <button
                  className={`store-front-customize-shirts-color-option ${
                    selectedColor === item.color ? "selected" : ""
                  }`}
                  style={{ backgroundColor: colorHexMap[item.color] || "#ccc" }} // Default to gray if not found
                  onClick={() => setSelectedColor(item.color)}
                />
                <div className="store-front-customize-shirts-color-option-name">
                  {item.color}
                </div>
              </div>
            ))}
        </div>

        {/* Size selection */}
        <label className="store-front-customize-shirts-label">
          Choose a size:
        </label>
        <select
          id="size-select"
          className="store-front-customize-shirts-size-quantity-forms"
          value={selectedSize}
          onChange={(e) => handleSizeChange(e.target.value)}
        >
          {availableSizes.map((size) => (
            <option key={size.db_name} value={size.db_name}>
              {size.display_name}
            </option>
          ))}
        </select>
        <label htmlFor="customization">Choose Customization:</label>
        <select
          id="customization"
          className="store-front-customize-shirts-size-quantity-forms"
          value={customizationOption.text}
          onChange={handleCustomizationOptionChange}
        >
          {customizationOptions.map((option, index) => (
            <option key={index} value={option.text}>
              {option.text}
            </option>
          ))}
        </select>
        <label className="store-front-customize-shirts-label">
          {`Custom Text on Back:`}
        </label>
        <input
          type="text"
          value={clothingText}
          placeholder="e.g. Name and Number"
          onChange={(e) => setClothingText(e.target.value)}
          className="store-front-customize-shirts-size-quantity-forms"
          disabled={customizationOption.upcharge === 0}
        />
        {/* Quantity selector */}
        <div className="store-front-customize-shirts-quantity-selector">
          <label htmlFor="quantity">Quantity:</label>
          <input
            type="number"
            id="quantity"
            value={quantity}
            min={1}
            onChange={handleQuantityChange}
            className="store-front-customize-shirts-size-quantity-forms"
          />
        </div>

        {/* Price display */}
        <div className="store-front-customize-shirts-price">
          {clothingItem && `$${clothingItem.price.toFixed(2)}`}
        </div>

        {/* Buttons at the bottom of the form */}
        <div className="store-front-customize-shirts-actions">
          <AddToCartButton
            item={clothingItem}
            participant={state.fundraiser_participant}
            disabled={!selectedSize || clothingItem.price === 0}
            className="store-front-customize-shirts-button add-to-cart"
          />
          <button
            className="store-front-customize-shirts-button continue-shopping"
            onClick={handleContinueShopping}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClothingForm;
